import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TablerIconsModule } from 'angular-tabler-icons';
import { debounceTime, distinctUntilChanged, map, Observable, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { AppointmentStatusType, AppointmentStatusTypeLabels } from 'src/app/common/enums/appointment-status-type';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { MaterialModule } from 'src/app/material.module';
import { LookupService } from 'src/app/services/lookup.service';
import { AppointmentDetailComponent } from '../../settings/appointment-tab/appointment-type/appointment-detail/appointment-detail.component';
import { AppointmentService } from '../appointment.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TimeConvertPipe } from 'src/app/pipe/time-convert.pipe';
import * as constants from 'src/app/common/constant/constants.type'
import { PatientDetailComponent } from '../../patient/detail/patient-detail.component';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import * as moment from 'moment';
import { Appointment, AppointmentSlot } from '../iappointment';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';

@Component({
  selector: 'app-add-appointment',
  imports: [
    MaterialModule,
    CommonModule,
    TablerIconsModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule
  ],
  providers: [
    DatePipe,
    TimeConvertPipe,
    provideNativeDateAdapter(),
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './add-appointment.component.html',
  styleUrl: './add-appointment.component.scss'
})
export class AddAppointmentComponent implements OnInit {

  filteredOptions: Observable<CommonDropDown[]>;
  options: CommonDropDown[] = [];
  AppointmentAddForm: FormGroup;
  patientAppointment: Appointment;
  public statusData = AppointmentStatusTypeLabels;
  public statusList = Object.values(AppointmentStatusType).filter(value => typeof value === 'number');
  appointmentTypeList: CommonDropDown[] = []; // Assuming this is the type for appointment types
  isUpdate: boolean = false;
  timeSlot: AppointmentSlot[] = [];
  private destroy$ = new Subject<void>();
  unavailableDays: string[] = []; // e.g. ['mon','wed']
  // Map day names to number (0=Sunday,...6=Saturday)
  private dayMap: Record<string, number> = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
  };
  @ViewChild(MatAutocompleteTrigger) autoTrigger!: MatAutocompleteTrigger;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  get isPatientSelected(): boolean {
    const patientValue = this.AppointmentAddForm?.get('patientId')?.value;
    return typeof patientValue === 'string' && patientValue.trim().length > 0;
  }

  constructor(
    private _lookupService: LookupService,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) private _data: { event: AppointmentSlot, selectedDate: Date, doctorId: number, slots: AppointmentSlot[] },
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<AddAppointmentComponent>,
    private _appointmentService: AppointmentService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private timeConvertPipe: TimeConvertPipe,
    private datePipe: DatePipe,
    private translationUtils: TranslationUtils
  ) {
    this.patientAppointment = this._data?.event?.appointment as Appointment;

    if (this.patientAppointment?.patientId) {
      this.isUpdate = true;
    } else {
      this.isUpdate = false;
    }
    this.timeSlot = this._data.slots;
  }

  ngOnInit(): void {
    this.fetchDoctorAvailability(this._data.doctorId, this._data.selectedDate);
    const statusFromApi = this.patientAppointment?.status;

    this.AppointmentAddForm = this.fb.group({
      patientId: [this.patientAppointment?.patientId || null, Validators.required],
      appointmentTypeId: [],
       status: [
        {
          value: statusFromApi
            ? AppointmentStatusType[statusFromApi as unknown as keyof typeof AppointmentStatusType]
            : AppointmentStatusType.Scheduled,
          disabled: true
        }
      ],
      appointDate: [this._data.selectedDate instanceof Date ? this._data.selectedDate : new Date(this._data.selectedDate)],
      appointTime: [this._data.event.time],
      // value: [this.patientAppointment?.appointment?.charge],
    });
    this._lookupService.appointmenttype$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((response) => {
        this.appointmentTypeList = response;
        const name = this.patientAppointment?.appointmentTypeName;
        if (name) {
          const appointmentTypeId = this.getAppointmentTypeIdByName(name);
          this.AppointmentAddForm.patchValue({
            appointmentTypeId: appointmentTypeId
          });
        } else {
          const appointmentTypeId = this.getAppointmentTypeIdByName(constants.APPOINTMENT_TYPENAME);
          this.AppointmentAddForm.patchValue({
            appointmentTypeId: appointmentTypeId
          });
        }
      });


    this.filteredOptions = this.AppointmentAddForm.get("patientId")!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(searchText => {
        const value = typeof searchText === 'string' ? searchText : searchText?.name || '';
        return this._lookupService.getPatient(value);
      }),
      tap(list => this.options = list)
    );

    if (this.patientAppointment?.patientId) {
      this._lookupService.getPatient().subscribe(list => {
        const match = list.find(x => x.id == this.patientAppointment.patientId);
        if (match) {
          this.AppointmentAddForm.patchValue({ patientId: match });
        }
      });
    }
  }

  private _filterName(value: string): CommonDropDown[] {
    const filterValue = value?.toString().toLowerCase();

    return this.options.filter((i) =>
      i.name?.toLowerCase().includes(filterValue)
    );
  }

  private isDayAvailable(dayOfWeek: number): boolean {
    if (!this.unavailableDays || this.unavailableDays.length === 0) {
      return true;
    }

    return !this.unavailableDays.some(unavailableDay => {
      const dayIndex = this.dayMap[unavailableDay.toLowerCase()];
      return dayIndex === dayOfWeek;
    });
  }

  displayFn(patient: any): string {
    if (typeof patient === 'string') {
      return patient;
    }
    return patient?.name || '';
  }

  openAppointmentTypeDialog() {
    this.dialog.open(AppointmentDetailComponent, {
      maxWidth: '70vh',
      width: '90%',
    }).afterClosed().subscribe((response) => {
      if (response) {
        this._lookupService.getAppointmentTypeLookup()
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe((response) => {
            this.appointmentTypeList = response;
          });
        this.AppointmentAddForm.get('appointmentTypeId')?.setValue(response.result.id);
      }
    });
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

  onSaveAppointment() {
    if (!this.AppointmentAddForm.valid) return;

    const appointDateControl = this.AppointmentAddForm.get('appointDate')?.value;
    const appointTimeControl = this.AppointmentAddForm.get('appointTime')?.value;

    const formattedDate = this.datePipe.transform(appointDateControl, constants.DATE_FORMAT);
    const formattedTime = this.timeConvertPipe.transform(appointTimeControl);
    const formValue = this.AppointmentAddForm.getRawValue();
    const appointmentData = {
      ...formValue,
      doctorId: this._data.doctorId,
      appointDate: formattedDate,
      appointTime: formattedTime,
      patientId: this.AppointmentAddForm.get('patientId')?.value.id
    };

    this._appointmentService.createAppointment(appointmentData).subscribe((response) => {
      if (response.isSuccess) {
        this.dialogRef.close(response);
        const message = this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.APPOINTMENT'));
        this.toastr.success(message);
      } else {
        const allErrors = Object.values(response.errors || {}).flat();
        this.toastr.error(allErrors.join('\n'));
      }
    });
  }

  onUpdateAppointment() {
    if (this.AppointmentAddForm.valid) {
      const appointDateControl = this.AppointmentAddForm.get('appointDate')?.value;
      const appointTimeControl = this.AppointmentAddForm.get('appointTime')?.value;

      const formattedDate = this.datePipe.transform(appointDateControl, constants.DATE_FORMAT);
      const formattedTime = this.timeConvertPipe.transform(appointTimeControl);
      const appointmentData = {
        appointDate: formattedDate ?? '',
        appointTime: formattedTime,
        appointmentTypeId: this.AppointmentAddForm.get('appointmentTypeId')?.value,
        doctorId: this._data.doctorId,
        id: this.patientAppointment?.appointmentId,
        status: this.AppointmentAddForm.get('status')?.value,
      };

      this._appointmentService.updateAppointment(appointmentData).subscribe((response) => { // formdata Enter empty form
        if (response.isSuccess) {
          this.dialogRef.close(response);
          this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('ENTITY.APPOINTMENT')));
        }
        else {
          const allErrors = Object.values(response.errors).flat();
          this.toastr.error(allErrors.join("\n"));
        }
      });

    }
  }

  getAppointmentStatusIdByName(name: string): number {
    return AppointmentStatusType[name as keyof typeof AppointmentStatusType];
  }

  getPatientName(id: number | null): string {
    const patient = this.options?.find(p => p.id === id);
    return patient ? patient.name : '';
  }

  getAppointmentTypeIdByName(name: string) {
    const type = this.appointmentTypeList.find(t => t.name == name);
    return type ? type.id : null;
  }

  onDateChange(dateEvent: MatDatepickerInputEvent<Date>) {
    if (!dateEvent.value) return;

    const selectedDate = dateEvent.value;
    const formattedDate = this.datePipe.transform(selectedDate, constants.DATE_FORMAT);

    this.fetchDoctorAvailability(this._data.doctorId, selectedDate);

    const appointmentSlotRequest = { date: formattedDate, doctorId: this._data.doctorId };

    this._appointmentService.getAppointmentSlot(appointmentSlotRequest).subscribe({
      next: (data) => {
        if (!data.result || data.result.daysAvailable.length === 0) {
          this.toastr.error(this.translate.instant('APPOINTMENT_RELATED.DOCTOR_AVAILABILITY'));
          this.timeSlot = [];
        } else {
          const availableSlots = data.result.timeSlots.filter((slot) => slot.appointment == null);
          if (availableSlots.length === 0) {
            this.toastr.error(this.translate.instant('APPOINTMENT_RELATED.ALREADY_BOOKED_SLOT'));
          }
          this.timeSlot = availableSlots;

          if (availableSlots.length === 0) {
            this.AppointmentAddForm.get('appointTime')?.setValue(null);
          }
        }
      },
      error: (error) => {
        console.error('Error fetching appointment slots:', error);
        this.toastr.error(this.translate.instant('APPOINTMENT_RELATED.DOCTOR_AVAILABILITY'));
        this.timeSlot = [];
      }
    });
  }

  shouldShowAddOption(value: any): boolean {
    if (!value) return false;

    let text = '';

    // If user is typing (string)
    if (typeof value === 'string') {
      text = value.trim().toLowerCase();
    }

    // If a patient object is in the control
    else if (typeof value === 'object' && value.name) {
      text = value.name.trim().toLowerCase();
    }

    if (!text) return false;

    // Check if typed name already exists
    return !this.options.some(opt => opt.name.toLowerCase() === text);
  }


  addPatient() {
    this.dialog.open(PatientDetailComponent, {
      height: '80vh',
      minWidth: '60vw',
      disableClose: true,
      data: {
        patientValue: this.AppointmentAddForm.get('patientId')?.value,
      }
    }).afterClosed().subscribe(response => {
      if (response) {
        this._lookupService.getPatient().subscribe(list => {
          this.options = list || [];
          const newPatient = list.find(p => p.id === response.result.id);
          if (newPatient) {
            this.AppointmentAddForm.get('patientId')?.setValue(newPatient);
          }
          setTimeout(() => this.autoTrigger.closePanel());
        });
      }
    })

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchDoctorAvailability(doctorId: number, date: Date) {
    this.unavailableDays = []; // reset
    const formattedDate = this.datePipe.transform(date, constants.DATE_FORMAT);

    this._appointmentService.getAppointmentSlot({ date: formattedDate, doctorId }).subscribe({
      next: (data) => {
        if (data.result.daysAvailable.length > 0) {
          const allAvailableDays = data.result.daysAvailable.map(day => day.toLowerCase());
          // unavailableDays = days not in availableDays
          this.unavailableDays = constants.DAYS.filter(d =>
            !allAvailableDays.includes(d.toLowerCase())
          );
        } else {
          this.unavailableDays = constants.DAYS;
        }
      },
      error: (error) => {
        this.unavailableDays = constants.DAYS;
      }
    });
  }

  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    const momentDate = moment(date);
    if (!momentDate.isValid()) return false;

    const dayOfWeek = momentDate.day();

    return this.isDayAvailable(dayOfWeek);
  };
}